import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { GraduationCap, X, Check } from 'lucide-react'
import { GrupoInstitucion, ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  isOpen: boolean
  onClose: () => void
  expediente: ExpedienteDespacho | null
  grupos: GrupoInstitucion[]
  onReasignar: (expId: string, nuevoGrupoId: number, nuevoGrupoNombre: string) => void
}

export const ModalReasignarSalon: React.FC<Props> = ({
  isOpen,
  onClose,
  expediente,
  grupos,
  onReasignar
}) => {
  const [grupoDestinoId, setGrupoDestinoId] = useState<number>(
    grupos[0]?.id_grupo || 1
  )

  if (!isOpen || !expediente) return null

  const handleConfirmar = (e: React.FormEvent) => {
    e.preventDefault()
    const grupoDestino = grupos.find((g) => g.id_grupo === Number(grupoDestinoId))
    if (!grupoDestino) return

    onReasignar(expediente.id, grupoDestino.id_grupo, grupoDestino.nombre)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden text-slate-100"
      >
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-white">Reasignar Salón</h3>
              <p className="text-xs text-slate-400">Mover expediente entre grupos académicos</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleConfirmar} className="p-5 space-y-4">
          <div className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800 text-xs space-y-1">
            <span className="text-[10px] font-bold uppercase text-slate-500 block">Expediente Seleccionado:</span>
            <p className="font-mono font-bold text-white text-sm">{expediente.folio}</p>
            <p className="text-slate-400 truncate">{expediente.titulo}</p>
            <p className="text-[11px] text-purple-400 font-medium pt-1">
              Salón Actual: <strong>{expediente.grupoNombre || 'Sin Salón Asignado'}</strong>
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 mb-2">
              Seleccionar Nuevo Salón / Grupo de Destino:
            </label>
            <select
              value={grupoDestinoId}
              onChange={(e) => setGrupoDestinoId(Number(e.target.value))}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-sm text-white focus:outline-none focus:border-purple-500 transition-colors"
            >
              {grupos.map((g) => (
                <option key={g.id_grupo} value={g.id_grupo}>
                  {g.nombre} {g.descripcion ? `(${g.descripcion})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="pt-2 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-purple-500/20 transition-all flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              <span>Confirmar Reasignación</span>
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default ModalReasignarSalon
